import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../api/PurchaseAPI/dailyIssueAPI';

const getErrorMessage = (err, fallback) =>
    err?.ErrorDetails?.ErrorMessage || err?.Message || err?.message || fallback;

// ── Async Thunks ─────────────────────────────────────────────────────────────

export const fetchCostCenters = createAsyncThunk(
    'dailyIssue/fetchCostCenters',
    async ({ uid, rid }, { rejectWithValue }) => {
        try {
            const res = await api.getCostCenterCodesDI(uid, rid);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load cost centers'));
        }
    }
);

export const fetchCategories = createAsyncThunk(
    'dailyIssue/fetchCategories',
    async (_, { rejectWithValue }) => {
        try {
            const res = await api.getCategoriesDI();
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load categories'));
        }
    }
);

export const fetchMainGrid = createAsyncThunk(
    'dailyIssue/fetchMainGrid',
    async ({ ccode, rid }, { rejectWithValue }) => {
        try {
            const res = await api.getItemsDailyIssueSK(ccode, rid);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load staged issue items'));
        }
    }
);

export const fetchSuggestions = createAsyncThunk(
    'dailyIssue/fetchSuggestions',
    async ({ pfx, typ, cat, cc }, { rejectWithValue }) => {
        try {
            const res = await api.getAutoCompleteItemTransfer(pfx, typ, cat, cc);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load suggestions'));
        }
    }
);

export const fetchLotDetails = createAsyncThunk(
    'dailyIssue/fetchLotDetails',
    async ({ itemcode, cccode }, { rejectWithValue }) => {
        try {
            const res = await api.getNewDIItemsIssueTempDetails(itemcode, cccode);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load item lot details'));
        }
    }
);

// Assigns lot quantities to the main (staged) grid. Success format from the API is
// "Submited,<code>" — anything else is an error message meant to be shown verbatim.
export const addTempIssueItems = createAsyncThunk(
    'dailyIssue/addTempIssueItems',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await api.saveTempNewItemsDailyIssue(payload);
            const raw = res?.Data || '';
            const [status, code] = raw.split(',');
            if (status !== 'Submited') {
                return rejectWithValue(raw || 'Failed to add items');
            }
            return { code };
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to add items'));
        }
    }
);

export const deleteIssueItem = createAsyncThunk(
    'dailyIssue/deleteIssueItem',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await api.deleteDailyIssueItems(payload);
            const status = res?.Data || '';
            if (status !== 'Submited') {
                return rejectWithValue(status || 'Failed to delete item');
            }
            return true;
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to delete item'));
        }
    }
);

// Success format is "Submited,<TransactionId>".
export const submitDailyIssue = createAsyncThunk(
    'dailyIssue/submitDailyIssue',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await api.saveDailyIssueItems(payload);
            const raw = res?.Data || '';
            const [status, refNo] = raw.split(',');
            if (status !== 'Submited') {
                return rejectWithValue(raw || 'Failed to submit daily issue');
            }
            return { refNo };
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to submit daily issue'));
        }
    }
);

// ── Slice ────────────────────────────────────────────────────────────────────

const initialState = {
    ccList: [],
    categoryList: [],
    mainGridItems: [],
    suggestions: [],
    lotDetails: [],
    addTempResult: null,
    submitResult: null,
    loading: {
        ccList: false,
        categoryList: false,
        mainGrid: false,
        suggestions: false,
        lotDetails: false,
        addTemp: false,
        deleteItem: false,
        submit: false,
    },
    errors: {
        ccList: null,
        categoryList: null,
        mainGrid: null,
        lotDetails: null,
        addTemp: null,
        deleteItem: null,
        submit: null,
    },
};

const dailyIssueSlice = createSlice({
    name: 'dailyIssue',
    initialState,
    reducers: {
        clearMainGrid:    (s) => { s.mainGridItems = []; s.errors.mainGrid = null; },
        clearSuggestions: (s) => { s.suggestions = []; },
        clearLotDetails:  (s) => { s.lotDetails = []; s.errors.lotDetails = null; },
        clearAddTempResult: (s) => { s.addTempResult = null; s.errors.addTemp = null; },
        clearSubmitResult:  (s) => { s.submitResult = null; s.errors.submit = null; },
        resetDailyIssue:  () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCostCenters.pending,   (s) => { s.loading.ccList = true; s.errors.ccList = null; })
            .addCase(fetchCostCenters.fulfilled, (s, a) => { s.loading.ccList = false; s.ccList = a.payload; })
            .addCase(fetchCostCenters.rejected,  (s, a) => { s.loading.ccList = false; s.errors.ccList = a.payload; });

        builder
            .addCase(fetchCategories.pending,   (s) => { s.loading.categoryList = true; s.errors.categoryList = null; })
            .addCase(fetchCategories.fulfilled, (s, a) => { s.loading.categoryList = false; s.categoryList = a.payload; })
            .addCase(fetchCategories.rejected,  (s, a) => { s.loading.categoryList = false; s.errors.categoryList = a.payload; });

        builder
            .addCase(fetchMainGrid.pending,   (s) => { s.loading.mainGrid = true; s.errors.mainGrid = null; })
            .addCase(fetchMainGrid.fulfilled, (s, a) => { s.loading.mainGrid = false; s.mainGridItems = a.payload; })
            .addCase(fetchMainGrid.rejected,  (s, a) => { s.loading.mainGrid = false; s.errors.mainGrid = a.payload; s.mainGridItems = []; });

        builder
            .addCase(fetchSuggestions.pending,   (s) => { s.loading.suggestions = true; })
            .addCase(fetchSuggestions.fulfilled, (s, a) => { s.loading.suggestions = false; s.suggestions = a.payload; })
            .addCase(fetchSuggestions.rejected,  (s) => { s.loading.suggestions = false; s.suggestions = []; });

        builder
            .addCase(fetchLotDetails.pending,   (s) => { s.loading.lotDetails = true; s.errors.lotDetails = null; })
            .addCase(fetchLotDetails.fulfilled, (s, a) => { s.loading.lotDetails = false; s.lotDetails = a.payload; })
            .addCase(fetchLotDetails.rejected,  (s, a) => { s.loading.lotDetails = false; s.errors.lotDetails = a.payload; s.lotDetails = []; });

        builder
            .addCase(addTempIssueItems.pending,   (s) => { s.loading.addTemp = true; s.errors.addTemp = null; s.addTempResult = null; })
            .addCase(addTempIssueItems.fulfilled, (s, a) => { s.loading.addTemp = false; s.addTempResult = a.payload; s.lotDetails = []; })
            .addCase(addTempIssueItems.rejected,  (s, a) => { s.loading.addTemp = false; s.errors.addTemp = a.payload; });

        builder
            .addCase(deleteIssueItem.pending,   (s) => { s.loading.deleteItem = true; s.errors.deleteItem = null; })
            .addCase(deleteIssueItem.fulfilled, (s) => { s.loading.deleteItem = false; })
            .addCase(deleteIssueItem.rejected,  (s, a) => { s.loading.deleteItem = false; s.errors.deleteItem = a.payload; });

        builder
            .addCase(submitDailyIssue.pending,   (s) => { s.loading.submit = true; s.errors.submit = null; s.submitResult = null; })
            .addCase(submitDailyIssue.fulfilled, (s, a) => { s.loading.submit = false; s.submitResult = a.payload; s.mainGridItems = []; })
            .addCase(submitDailyIssue.rejected,  (s, a) => { s.loading.submit = false; s.errors.submit = a.payload; });
    },
});

export const {
    clearMainGrid,
    clearSuggestions,
    clearLotDetails,
    clearAddTempResult,
    clearSubmitResult,
    resetDailyIssue,
} = dailyIssueSlice.actions;

// ── Selectors ────────────────────────────────────────────────────────────────
export const selectDICCList        = (s) => s.dailyIssueCreation.ccList;
export const selectDICategoryList  = (s) => s.dailyIssueCreation.categoryList;
export const selectDIMainGridItems = (s) => s.dailyIssueCreation.mainGridItems;
export const selectDISuggestions   = (s) => s.dailyIssueCreation.suggestions;
export const selectDILotDetails    = (s) => s.dailyIssueCreation.lotDetails;
export const selectDIAddTempResult = (s) => s.dailyIssueCreation.addTempResult;
export const selectDISubmitResult  = (s) => s.dailyIssueCreation.submitResult;
export const selectDILoading       = (s) => s.dailyIssueCreation.loading;
export const selectDIErrors        = (s) => s.dailyIssueCreation.errors;

export default dailyIssueSlice.reducer;
